import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { ChevronUp, ChevronDown, RotateCcw, GripVertical, Menu } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { NAV_ITEMS } from '../Layout';

const STORAGE_KEY = 'nav_order';

// Builds the current ordered list: saved order first, then any items not yet
// in the saved order (so newly added menus still appear).
function buildOrdered() {
  let saved = [];
  try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { saved = []; }
  const byHref = Object.fromEntries(NAV_ITEMS.map(n => [n.href, n]));
  const seen = new Set();
  const result = [];
  saved.forEach(href => { if (byHref[href] && !seen.has(href)) { result.push(byHref[href]); seen.add(href); } });
  NAV_ITEMS.forEach(n => { if (!seen.has(n.href)) result.push(n); });
  return result;
}

const MenuOrderTab = () => {
  const [items, setItems] = useState(buildOrdered);

  const persist = (list) => {
    setItems(list);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.map(i => i.href)));
    window.dispatchEvent(new Event('nav-order-changed'));
  };

  const move = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const list = [...items];
    [list[index], list[target]] = [list[target], list[index]];
    persist(list);
  };

  const moveToTop = (index) => {
    if (index === 0) return;
    const list = [...items];
    const [it] = list.splice(index, 1);
    list.unshift(it);
    persist(list);
  };

  const reset = () => {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new Event('nav-order-changed'));
    setItems(buildOrdered());
    toast({ title: 'Menu order reset to default' });
  };

  return (
    <Card data-testid="menu-order-tab">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Menu className="h-4 w-4 text-violet-600" /> Sidebar Menu Order
            </CardTitle>
            <CardDescription>
              Arrange the left menu to your liking — move your most-used pages to the top.
              This is saved on this device.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={reset} data-testid="menu-order-reset">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg divide-y max-h-[65vh] overflow-y-auto">
          {items.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div key={item.href} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50" data-testid={`menu-order-row-${item.href}`}>
                <GripVertical className="h-4 w-4 text-gray-300 flex-shrink-0" />
                <span className="text-xs text-gray-400 w-6 flex-shrink-0 tabular-nums">{idx + 1}</span>
                {Icon && <Icon className="h-4 w-4 text-gray-500 flex-shrink-0" />}
                <span className="flex-1 text-sm font-medium text-gray-800 truncate">{item.name}</span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled={idx === 0}
                    onClick={() => moveToTop(idx)} title="Move to top" data-testid={`menu-top-${item.href}`}>
                    <span className="text-[10px] font-bold">TOP</span>
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled={idx === 0}
                    onClick={() => move(idx, -1)} title="Move up" data-testid={`menu-up-${item.href}`}>
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled={idx === items.length - 1}
                    onClick={() => move(idx, 1)} title="Move down" data-testid={`menu-down-${item.href}`}>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default MenuOrderTab;
